import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { CurrencyInput } from '@/components/ui/currency-input'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { useToast } from '@/hooks/use-toast'
import { Loader2, Plus } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { SortableBenefitLine } from './SortableBenefitLine'

// Setting keys
const BOOKING_SETTINGS = {
  MINIMUM_NOTICE: 'booking_minimum_notice',
  MAXIMUM_ADVANCE: 'booking_maximum_advance',
  AUTO_CONFIRM: 'booking_auto_confirm',
  CANCELLATION_WINDOW: 'booking_cancellation_window',
  CANCELLATION_FEE: 'booking_cancellation_fee',
  DEFAULT_PICKUP_TIME: 'booking_default_pickup_time',
  RETURN_TRIP_DISCOUNT: 'booking_return_trip_discount',
  RETURN_TRIP_EDITABLE_ADDRESSES: 'booking_return_trip_editable_addresses',
  CAPACITY_DISCLAIMER: 'booking_capacity_disclaimer',
  SIMPLIFIED_PRICING: 'booking_simplified_pricing',
  QUOTE_VALIDITY_DAYS: 'quote_validity_days',
  CUSTOM_QUOTES_ENABLED: 'booking_custom_quotes_enabled',
  COLLECT_USER_INFO_EARLY: 'booking_collect_user_info_early',
  POINT_TO_POINT_ENABLED: 'booking_point_to_point_enabled',
  HOURLY_ENABLED: 'booking_hourly_enabled',
  BENEFITS_ENABLED: 'booking_benefits_enabled',
  BENEFITS_TITLE: 'booking_benefits_title',
  BENEFITS_ITEMS: 'booking_benefits_items',
} as const

// Defaults for the "Enjoy hassle free rides" benefits card on the vehicle-list
// page. Kept in sync with apps/viewer/src/lib/booking/benefits.ts (the viewer is
// the source of truth for rendered fallbacks; these drive the Builder form).
const DEFAULT_BENEFITS_TITLE = 'Enjoy hassle free rides'
const DEFAULT_BENEFITS_ITEMS = [
  'All prices include tolls',
  'Always on time',
  'Professional drivers',
  'Best-in-class vehicles',
]

// Parse the JSON-encoded benefit items stored in account_settings, falling back
// to the defaults on empty/invalid input.
function parseBenefitsItems(raw: string | undefined): string[] {
  if (!raw) return DEFAULT_BENEFITS_ITEMS
  try {
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) {
      const items = parsed.map((x) => String(x ?? '')).filter((x) => x.trim().length > 0)
      return items.length > 0 ? items : DEFAULT_BENEFITS_ITEMS
    }
  } catch {
    // fall through
  }
  return DEFAULT_BENEFITS_ITEMS
}

// Schema
const bookingSchema = z.object({
  minimum_notice: z.coerce.number().min(0, 'Must be 0 or greater').optional(),
  maximum_advance: z.coerce.number().min(1, 'Must be at least 1 day').optional(),
  auto_confirm: z.boolean().default(false),
  cancellation_window: z.coerce.number().min(0, 'Must be 0 or greater').optional(),
  cancellation_fee: z.coerce.number().min(0, 'Must be 0 or greater').optional(),
  default_pickup_time: z.string()
    .regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Invalid time format (HH:mm)')
    .optional()
    .default('09:00'),
  return_trip_discount: z.coerce.number().min(0).max(50).optional(),
  return_trip_editable_addresses: z.boolean().default(false),
  capacity_disclaimer: z.string().optional(),
  simplified_pricing: z.boolean().default(false),
  quote_validity_days: z.coerce.number().min(1, 'Must be at least 1 day').max(30, 'Must be 30 days or less').optional(),
  custom_quotes_enabled: z.boolean().default(false),
  collect_user_info_early: z.boolean().default(false),
  point_to_point_enabled: z.boolean().default(true),
  hourly_enabled: z.boolean().default(true),
  benefits_enabled: z.boolean().default(true),
  benefits_title: z.string().optional(),
  benefits_items: z.array(z.string()).optional(),
}).refine((data) => data.point_to_point_enabled || data.hourly_enabled, {
  message: 'At least one booking type must remain enabled',
  path: ['hourly_enabled'],
})

type BookingFormData = z.infer<typeof bookingSchema>

export function BookingPage() {
  const { account } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const activeAccount = isImpersonating ? impersonatedAccount : account

  // Fetch account settings
  const { data: settings, isLoading } = useQuery({
    queryKey: ['account-settings', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return {}

      const { data, error } = await supabase
        .from('account_settings')
        .select('*')
        .eq('account_id', activeAccount.id)

      if (error) throw error

      const settingsMap: Record<string, string> = {}
      data?.forEach((setting) => {
        settingsMap[setting.key] = setting.value || ''
      })
      return settingsMap
    },
    enabled: !!activeAccount?.id,
  })

  // Form
  const form = useForm<BookingFormData>({
    resolver: zodResolver(bookingSchema),
    defaultValues: {
      minimum_notice: 24,
      maximum_advance: 90,
      auto_confirm: false,
      cancellation_window: 24,
      cancellation_fee: 50,
      default_pickup_time: '09:00',
      return_trip_discount: 0,
      return_trip_editable_addresses: false,
      capacity_disclaimer: 'This vehicle accommodates {{passengers}} passengers and {{luggage}} pieces of luggage. Please ensure this meets your needs before booking.',
      simplified_pricing: false,
      quote_validity_days: 7,
      custom_quotes_enabled: false,
      collect_user_info_early: false,
      point_to_point_enabled: true,
      hourly_enabled: true,
      benefits_enabled: true,
      benefits_title: DEFAULT_BENEFITS_TITLE,
      benefits_items: DEFAULT_BENEFITS_ITEMS,
    },
  })

  const benefitsEnabled = form.watch('benefits_enabled')
  const benefitsItems = form.watch('benefits_items') ?? []

  const setBenefitsItems = (items: string[]) =>
    form.setValue('benefits_items', items, { shouldDirty: true })
  const updateBenefitItem = (index: number, value: string) =>
    setBenefitsItems(benefitsItems.map((item, i) => (i === index ? value : item)))
  const addBenefitItem = () => setBenefitsItems([...benefitsItems, ''])
  const removeBenefitItem = (index: number) =>
    setBenefitsItems(benefitsItems.filter((_, i) => i !== index))

  // Drag-to-reorder for benefit lines (mirrors ArrayFieldEditor's dnd-kit setup)
  const benefitItemIds = benefitsItems.map((_, index) => `benefit-${index}`)
  const benefitSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
  const handleBenefitDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      const oldIndex = benefitItemIds.indexOf(active.id as string)
      const newIndex = benefitItemIds.indexOf(over.id as string)
      if (oldIndex !== -1 && newIndex !== -1) {
        setBenefitsItems(arrayMove([...benefitsItems], oldIndex, newIndex))
      }
    }
  }

  // Update form when settings are loaded
  useEffect(() => {
    if (settings) {
      form.reset({
        minimum_notice: settings[BOOKING_SETTINGS.MINIMUM_NOTICE] ? Number(settings[BOOKING_SETTINGS.MINIMUM_NOTICE]) : 24,
        maximum_advance: settings[BOOKING_SETTINGS.MAXIMUM_ADVANCE] ? Number(settings[BOOKING_SETTINGS.MAXIMUM_ADVANCE]) : 90,
        auto_confirm: settings[BOOKING_SETTINGS.AUTO_CONFIRM] === 'true',
        cancellation_window: settings[BOOKING_SETTINGS.CANCELLATION_WINDOW] ? Number(settings[BOOKING_SETTINGS.CANCELLATION_WINDOW]) : 24,
        cancellation_fee: settings[BOOKING_SETTINGS.CANCELLATION_FEE] ? Number(settings[BOOKING_SETTINGS.CANCELLATION_FEE]) : 50,
        default_pickup_time: settings[BOOKING_SETTINGS.DEFAULT_PICKUP_TIME] || '09:00',
        return_trip_discount: settings[BOOKING_SETTINGS.RETURN_TRIP_DISCOUNT] ? Number(settings[BOOKING_SETTINGS.RETURN_TRIP_DISCOUNT]) : 0,
        return_trip_editable_addresses: settings[BOOKING_SETTINGS.RETURN_TRIP_EDITABLE_ADDRESSES] === 'true',
        capacity_disclaimer: settings[BOOKING_SETTINGS.CAPACITY_DISCLAIMER] || 'This vehicle accommodates {{passengers}} passengers and {{luggage}} pieces of luggage. Please ensure this meets your needs before booking.',
        simplified_pricing: settings[BOOKING_SETTINGS.SIMPLIFIED_PRICING] === 'true',
        quote_validity_days: settings[BOOKING_SETTINGS.QUOTE_VALIDITY_DAYS] ? Number(settings[BOOKING_SETTINGS.QUOTE_VALIDITY_DAYS]) : 7,
        custom_quotes_enabled: settings[BOOKING_SETTINGS.CUSTOM_QUOTES_ENABLED] === 'true',
        collect_user_info_early: settings[BOOKING_SETTINGS.COLLECT_USER_INFO_EARLY] === 'true',
        // Default true: a missing row means the tab is enabled
        point_to_point_enabled: settings[BOOKING_SETTINGS.POINT_TO_POINT_ENABLED] !== 'false',
        hourly_enabled: settings[BOOKING_SETTINGS.HOURLY_ENABLED] !== 'false',
        // Default true: a missing row means the benefits card is shown
        benefits_enabled: settings[BOOKING_SETTINGS.BENEFITS_ENABLED] !== 'false',
        benefits_title: settings[BOOKING_SETTINGS.BENEFITS_TITLE] || DEFAULT_BENEFITS_TITLE,
        benefits_items: parseBenefitsItems(settings[BOOKING_SETTINGS.BENEFITS_ITEMS]),
      })
    }
  }, [settings, form])

  // Mutation
  const mutation = useMutation({
    mutationFn: async (data: BookingFormData) => {
      if (!activeAccount?.id) throw new Error('No account found')

      const settingsToUpsert = [
        {
          account_id: activeAccount.id,
          key: BOOKING_SETTINGS.MINIMUM_NOTICE,
          value: String(data.minimum_notice ?? 24),
          description: 'Minimum hours notice required for booking',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: BOOKING_SETTINGS.MAXIMUM_ADVANCE,
          value: String(data.maximum_advance ?? 90),
          description: 'Maximum days in advance to accept bookings',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: BOOKING_SETTINGS.AUTO_CONFIRM,
          value: String(data.auto_confirm),
          description: 'Automatically confirm new bookings',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: BOOKING_SETTINGS.CANCELLATION_WINDOW,
          value: String(data.cancellation_window ?? 24),
          description: 'Hours before pickup for free cancellation',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: BOOKING_SETTINGS.CANCELLATION_FEE,
          value: String(data.cancellation_fee ?? 50),
          description: 'Fee for late cancellations',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: BOOKING_SETTINGS.DEFAULT_PICKUP_TIME,
          value: data.default_pickup_time || '09:00',
          description: 'Default pickup time shown in booking form',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: BOOKING_SETTINGS.RETURN_TRIP_DISCOUNT,
          value: String(data.return_trip_discount ?? 0),
          description: 'Percentage discount for return trip bookings',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: BOOKING_SETTINGS.RETURN_TRIP_EDITABLE_ADDRESSES,
          value: String(data.return_trip_editable_addresses),
          description: 'Allow customers to edit return-trip pickup/dropoff addresses in the booking widget',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: BOOKING_SETTINGS.CAPACITY_DISCLAIMER,
          value: data.capacity_disclaimer || '',
          description: 'Vehicle capacity disclaimer shown on booking page',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: BOOKING_SETTINGS.SIMPLIFIED_PRICING,
          value: String(data.simplified_pricing),
          description: 'Show only total price during checkout',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: BOOKING_SETTINGS.QUOTE_VALIDITY_DAYS,
          value: String(data.quote_validity_days ?? 7),
          description: 'Number of days a quote remains valid before expiring',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: BOOKING_SETTINGS.CUSTOM_QUOTES_ENABLED,
          value: String(data.custom_quotes_enabled),
          description: 'Allow vehicles to be priced by hand through the Get Quote flow',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: BOOKING_SETTINGS.COLLECT_USER_INFO_EARLY,
          value: String(data.collect_user_info_early),
          description: 'Collect user info (name, email, phone) on the booking form before vehicle selection',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: BOOKING_SETTINGS.POINT_TO_POINT_ENABLED,
          value: String(data.point_to_point_enabled),
          description: 'Show the Point to Point tab on the booking widget',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: BOOKING_SETTINGS.HOURLY_ENABLED,
          value: String(data.hourly_enabled),
          description: 'Show the Hourly tab on the booking widget',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: BOOKING_SETTINGS.BENEFITS_ENABLED,
          value: String(data.benefits_enabled),
          description: 'Show the "Enjoy hassle free rides" benefits card on the vehicle-list page',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: BOOKING_SETTINGS.BENEFITS_TITLE,
          // Empty falls back to the default heading on the viewer
          value: (data.benefits_title ?? '').trim(),
          description: 'Heading for the benefits card on the vehicle-list page',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: BOOKING_SETTINGS.BENEFITS_ITEMS,
          // JSON array of non-empty lines; empty array stored as '' so the viewer
          // falls back to the default benefit lines
          value: (() => {
            const items = (data.benefits_items ?? [])
              .map((s) => s.trim())
              .filter((s) => s.length > 0)
            return items.length > 0 ? JSON.stringify(items) : ''
          })(),
          description: 'Benefit lines (JSON) for the benefits card on the vehicle-list page',
          is_secret: false,
        },
      ]

      for (const setting of settingsToUpsert) {
        const { error } = await supabase
          .from('account_settings')
          .upsert(setting, { onConflict: 'account_id,key' })

        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-settings', activeAccount?.id] })
      toast({
        title: 'Booking settings saved',
        description: 'Your booking settings have been updated.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-64" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-24" />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Booking Settings</CardTitle>
          <CardDescription>
            Configure how bookings are handled on your website
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit((data) => mutation.mutate(data))}
              className="space-y-6"
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="minimum_notice"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Minimum Notice (hours)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={0}
                          placeholder="24"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Minimum hours before pickup required
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="maximum_advance"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Maximum Advance (days)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={1}
                          placeholder="90"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Maximum days in advance to accept bookings
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="quote_validity_days"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Quote Validity Period (days)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={1}
                        max={30}
                        placeholder="7"
                        className="w-32"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Number of days a quote remains valid before expiring. Customers can resume
                      their booking using a unique link until the quote expires. Default is 7 days.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="custom_quotes_enabled"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Custom Quotes (Get Quote)</FormLabel>
                      <FormDescription>
                        The master switch for hand-priced trips. With it on, each vehicle
                        decides for itself under Vehicles &gt; Edit Vehicle &gt; Visibility —
                        either always quoted, or quoted past a mileage you set for that
                        vehicle. Customers then see a &quot;Get Quote&quot; button instead of a
                        price, and you set the price from a link sent to your phone and email.
                        With it off, no vehicle is ever quoted, whatever it has configured.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="default_pickup_time"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Default Pickup Time</FormLabel>
                    <FormControl>
                      <div className="flex items-center gap-2">
                        <Input
                          type="time"
                          className="w-32"
                          {...field}
                          value={field.value || '09:00'}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => field.onChange('09:00')}
                        >
                          Reset to default
                        </Button>
                      </div>
                    </FormControl>
                    <FormDescription>
                      The default time shown in the booking form on your website.
                      Customers can change this when booking.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="return_trip_discount"
                render={({ field }) => (
                  <FormItem className="rounded-lg border p-4">
                    <FormLabel className="text-base">Return Trip Discount</FormLabel>
                    <FormDescription>
                      Customers can add a return trip when booking point-to-point service.
                      Set a discount percentage to incentivize return trip bookings. The discount
                      is applied only to the return leg of the trip. Leave at 0 for no discount.
                    </FormDescription>
                    <FormControl>
                      <div className="flex items-center gap-2 mt-2">
                        <Input
                          type="number"
                          min={0}
                          max={50}
                          placeholder="0"
                          className="w-24"
                          {...field}
                        />
                        <span className="text-muted-foreground">%</span>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="return_trip_editable_addresses"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Allow customers to edit return trip addresses</FormLabel>
                      <FormDescription>
                        When enabled, the booking widget lets customers specify different
                        pickup/dropoff addresses for the return leg. Off by default — return
                        leg auto-reverses the outbound.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="collect_user_info_early"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Collect User Info on Booking Form</FormLabel>
                      <FormDescription>
                        Ask for customer name, email, and phone number on the initial booking form.
                        This information will be pre-filled at checkout.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="point_to_point_enabled"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Point to Point Bookings</FormLabel>
                      <FormDescription>
                        Show the Point to Point tab on the booking widget. Turn off if you
                        only offer hourly charters.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="hourly_enabled"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Hourly Bookings</FormLabel>
                      <FormDescription>
                        Show the Hourly tab on the booking widget. Turn off if you only
                        offer point-to-point trips. At least one booking type must stay on.
                      </FormDescription>
                      <FormMessage />
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="capacity_disclaimer"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Vehicle Capacity Disclaimer</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="This vehicle accommodates {{passengers}} passengers and {{luggage}} pieces of luggage."
                        className="resize-none"
                        rows={3}
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Shown on the booking details page. Use <code className="bg-muted px-1 rounded">{'{{passengers}}'}</code> and <code className="bg-muted px-1 rounded">{'{{luggage}}'}</code> as placeholders for vehicle capacity. Leave empty to hide the disclaimer.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="rounded-lg border p-4 space-y-4">
                <FormField
                  control={form.control}
                  name="benefits_enabled"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Benefits Card</FormLabel>
                        <FormDescription>
                          Show the &quot;Enjoy hassle free rides&quot; card in the booking sidebar
                          (vehicle-list page). Customize the heading and lines below.
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {benefitsEnabled && (
                  <>
                    <FormField
                      control={form.control}
                      name="benefits_title"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Heading</FormLabel>
                          <FormControl>
                            <Input
                              placeholder={DEFAULT_BENEFITS_TITLE}
                              {...field}
                              value={field.value ?? ''}
                            />
                          </FormControl>
                          <FormDescription>
                            Leave empty to use the default: &quot;{DEFAULT_BENEFITS_TITLE}&quot;
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="space-y-2">
                      <div className="text-sm font-medium leading-none">Benefit Lines</div>
                      <p className="text-sm text-muted-foreground">
                        Each line appears with a checkmark. Leave all lines empty to use the
                        defaults.
                      </p>
                      <DndContext
                        sensors={benefitSensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleBenefitDragEnd}
                      >
                        <SortableContext
                          items={benefitItemIds}
                          strategy={verticalListSortingStrategy}
                        >
                          <div className="space-y-2">
                            {benefitsItems.map((item, index) => (
                              <SortableBenefitLine
                                key={benefitItemIds[index]}
                                id={benefitItemIds[index]}
                                value={item}
                                placeholder={
                                  DEFAULT_BENEFITS_ITEMS[index] ?? 'Benefit line'
                                }
                                onChange={(value) => updateBenefitItem(index, value)}
                                onRemove={() => removeBenefitItem(index)}
                              />
                            ))}
                          </div>
                        </SortableContext>
                      </DndContext>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addBenefitItem}
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Add benefit
                      </Button>
                    </div>
                  </>
                )}
              </div>

              <FormField
                control={form.control}
                name="simplified_pricing"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Simplified Pricing Display</FormLabel>
                      <FormDescription>
                        Show only the total price during checkout. Hide itemized breakdown (base fare, taxes, fees, etc.)
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="auto_confirm"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Auto-Confirm Bookings</FormLabel>
                      <FormDescription>
                        Automatically confirm new bookings without manual review
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="cancellation_window"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Free Cancellation Window (hours)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={0}
                          placeholder="24"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Hours before pickup for free cancellation
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="cancellation_fee"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Late Cancellation Fee</FormLabel>
                      <FormControl>
                        <CurrencyInput
                          placeholder="50.00"
                          value={field.value}
                          onChange={(value) => field.onChange(value ?? 0)}
                        />
                      </FormControl>
                      <FormDescription>
                        Fee charged for late cancellations
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save Changes
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  )
}
